/**
 * Checks if the user is near the bottom of a scrollable container
 * @param containerEl - The scrollable container element
 * @param threshold - Distance in pixels from bottom to consider "near" (default: 100)
 * @returns true if within threshold pixels of bottom, false otherwise
 */
export const isNearBottom = (
  containerEl: HTMLElement | null,
  threshold: number = 100
): boolean => {
  if (!containerEl) return false;

  const { scrollTop, clientHeight, scrollHeight } = containerEl;
  const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

  return distanceFromBottom < threshold;
};

/**
 * Gets the distance from the bottom of a scrollable container
 * @param containerEl - The scrollable container element
 * @returns Distance in pixels from bottom, or 0 if container is null
 */
export const getDistanceFromBottom = (
  containerEl: HTMLElement | null
): number => {
  if (!containerEl) return 0;

  const { scrollTop, clientHeight, scrollHeight } = containerEl;
  return scrollHeight - scrollTop - clientHeight;
};

/**
 * Options for scrolling to an element
 */
export interface ScrollToElementOptions {
  /** Initial delay before first scroll attempt (ms) */
  initialDelay?: number;
  /** Delay between retry attempts (ms) */
  retryDelay?: number;
  /** Maximum number of retry attempts */
  maxRetries?: number;
  /** Scroll behavior */
  behavior?: ScrollBehavior;
  /** Block alignment */
  block?: ScrollLogicalPosition;
}

/**
 * Scrolls to an element by ID with retry mechanism to handle DOM rendering delays
 * @param elementId - The ID of the element to scroll to
 * @param options - Configuration options for scrolling
 * @returns Cleanup function to cancel pending scroll operations
 */
export const scrollToElementWithRetry = (
  elementId: string,
  options: ScrollToElementOptions = {}
): (() => void) => {
  const {
    initialDelay = 150,
    retryDelay = 100,
    maxRetries = 5,
    behavior = "smooth",
    block = "center",
  } = options;

  let timeoutId: NodeJS.Timeout | null = null;
  let rafId: number | null = null;
  let retryCount = 0;

  /**
   * Attempts to scroll to the element
   * Uses retry mechanism to handle DOM rendering delays
   */
  const scrollToElement = (): void => {
    const element = document.getElementById(elementId);

    if (element) {
      // Smooth scroll to center the element
      element.scrollIntoView({ behavior, block });

      // Use double RAF to ensure browser has completed layout and paint
      // This fixes timing issues where the element might not be fully rendered
      rafId = requestAnimationFrame(() => {
        rafId = requestAnimationFrame(() => {
          // Final precise scroll to ensure element is centered
          element.scrollIntoView({ behavior: "auto", block });
        });
      });
    } else if (retryCount < maxRetries) {
      // Retry if element not found (DOM might not be updated yet)
      retryCount++;
      timeoutId = setTimeout(scrollToElement, retryDelay);
    }
  };

  // Initial scroll attempt with delay to allow DOM to update
  timeoutId = setTimeout(scrollToElement, initialDelay);

  // Return cleanup function to cancel pending timeouts and animation frames
  return () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
    }
  };
};
